import React, { useState } from 'react';
import {
  Box,
  Fab,
  Badge,
  Zoom,
  Paper,
  IconButton,
  Typography,
  Drawer
} from '@mui/material';
import {
  Chat,
  Close,
  SmartToy,
  Menu
} from '@mui/icons-material';
import BurtChat from './BurtChat';
import ChatHistorySidebar from './ChatHistorySidebar';
import { saveChatSession, loadChatSession } from '../utils/burtChatUtils';
import { useFamilyId } from '../contexts/FamilyIdContext';
import { useAuth } from '../contexts/AuthContext';

function BurtChatWidget({ tasks, completedTasks, familyMembers, shoppingList, familyData, currentUser, addShoppingItem }) {
  const [isOpen, setIsOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const { familyId } = useFamilyId();
  const { currentUser: authUser } = useAuth();

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const handleNewChat = () => {
    setCurrentSession(null);
    setMessages([]);
    setSidebarOpen(false);
  };

  const handleSelectSession = async (session) => {
    try {
      const fullSession = await loadChatSession(familyId, session.id);
      if (fullSession) {
        setCurrentSession(fullSession);
        setMessages(fullSession.messages || []);
        setSidebarOpen(false); // Close sidebar on mobile after selection
      }
    } catch (error) {
      console.error('Failed to load session:', error);
    }
  };

  const handleSaveMessages = async (newMessages) => {
    setMessages(newMessages);
    // Save every 3 messages to reduce Firestore writes
    if (newMessages.length % 3 === 0 || newMessages.length === 1) {
      try {
        const sessionId = await saveChatSession(
          familyId,
          authUser.uid,
          newMessages,
          currentSession?.id
        );
        if (!currentSession) {
          setCurrentSession({ id: sessionId });
        }
      } catch (error) {
        console.error('Failed to save chat session:', error);
      }
    }
  };

  return (
    <>
      {/* Floating Chat Window */}
      <Zoom in={isOpen}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: { xs: 16, sm: 100 },
            right: { xs: 16, sm: 88 },
            left: { xs: 16, sm: 'auto' }, // Fix left cutoff on mobile
            width: { xs: 'auto', sm: 420 },
            height: { xs: 'calc(100vh - 80px)', sm: 600 },
            maxHeight: { xs: 'calc(100vh - 80px)', sm: 'calc(100vh - 150px)' },
            display: isOpen ? 'flex' : 'none',
            flexDirection: 'column',
            zIndex: 1300,
            overflow: 'hidden',
            borderRadius: 3
          }}
        >
          {/* Header with Close Button and Sidebar Toggle */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              background: 'linear-gradient(45deg, #6A1B9A, #8E24AA)',
              color: 'white',
              flexShrink: 0
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                sx={{ color: 'white', display: { xs: 'flex', sm: 'none' } }}
              >
                <Menu />
              </IconButton>
              <SmartToy />
              <Typography variant="h6" fontWeight="bold">
                Ask Burt
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                size="small"
                onClick={handleNewChat}
                sx={{ color: 'white' }}
                title="New Chat"
              >
                <Chat />
              </IconButton>
              <IconButton
                onClick={toggleChat}
                sx={{ color: 'white', minWidth: 48, minHeight: 48 }}
              >
                <Close />
              </IconButton>
            </Box>
          </Box>

          {/* Chat Content with Sidebar */}
          <Box sx={{ 
            flexGrow: 1, 
            overflow: 'hidden',
            display: 'flex',
            minHeight: 0,
            bgcolor: 'background.paper'
          }}>
            {/* Sidebar Drawer (mobile) or permanent (desktop) */}
            <Drawer
              variant="permanent"
              sx={{
                width: sidebarOpen ? 280 : 0,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                  width: 280,
                  boxSizing: 'border-box',
                  borderRight: 'none',
                  position: 'relative',
                },
                display: { xs: sidebarOpen ? 'block' : 'none', sm: 'block' },
              }}
            >
              <ChatHistorySidebar
                onSelectSession={handleSelectSession}
                onNewChat={handleNewChat}
                selectedSessionId={currentSession?.id}
              />
            </Drawer>

            {/* Main Chat Area */}
            <Box sx={{ 
              flexGrow: 1, 
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}>
              <BurtChat
                tasks={tasks}
                completedTasks={completedTasks}
                familyMembers={familyMembers}
                shoppingList={shoppingList}
                familyData={familyData}
                currentUser={currentUser}
                addShoppingItem={addShoppingItem}
                initialMessages={messages}
                onMessagesChange={handleSaveMessages}
                currentSessionId={currentSession?.id}
              />
            </Box>
          </Box>
        </Paper>
      </Zoom>

      {/* Floating Action Button */}
      <Zoom in={!isOpen}>
        <Fab
          color="secondary"
          aria-label="chat with burt"
          onClick={toggleChat}
          sx={{
            position: 'fixed',
            bottom: 90,
            right: 88, // Center over Add Task button (24px + 64px button width)
            zIndex: 1200,
            background: 'linear-gradient(45deg, #6A1B9A, #8E24AA, #AB47BC)',
            '&:hover': {
              background: 'linear-gradient(45deg, #4A0072, #6A1B9A, #8E24AA)',
            }
          }}
        >
          <Badge badgeContent="AI" color="error" variant="dot">
            <Chat />
          </Badge>
        </Fab>
      </Zoom>
    </>
  );
}

export default BurtChatWidget;

